import type { CSSProperties } from "react";
import "@/styles/knowledge-brain.css";
import { confidenceColor } from "@/lib/knowledge/topicClusters";
import { cn } from "@/lib/utils";
import type { TopicCluster } from "@/types/knowledge/knowledge-brain";

interface TopicClusterMapProps {
  clusters: TopicCluster[];
  selectedClusterId: string | null;
  onSelect: (cluster: TopicCluster) => void;
}

const BAND_LABEL: Record<TopicCluster["band"], string> = {
  strong: "Well covered",
  steady: "Holding up",
  thin: "Needs more",
};

/** Bubble diameter in px, scaled by document count against the biggest cluster. */
const sizeFor = (count: number, max: number): number => {
  const min = 84;
  const span = 76;
  if (max <= 1) return min + span;
  const ratio = Math.sqrt(count / max);
  return Math.round(min + span * ratio);
};

/**
 * The Genie's knowledge as clustered topics — sized by coverage, coloured along
 * the brand gradient by confidence. Clicking a cluster filters the document
 * tools below the fold.
 */
const TopicClusterMap = ({ clusters, selectedClusterId, onSelect }: TopicClusterMapProps) => {
  if (clusters.length === 0) return null;

  const max = Math.max(...clusters.map((cluster) => cluster.documentCount));

  return (
    <ul
      className="flex flex-wrap items-end justify-center gap-3 sm:gap-4"
      aria-label="Knowledge topics by coverage"
    >
      {clusters.map((cluster, index) => {
        const size = sizeFor(cluster.documentCount, max);
        const color = confidenceColor(cluster.confidence);
        const isSelected = selectedClusterId === cluster.id;

        return (
          <li key={cluster.id}>
            <button
              type="button"
              onClick={() => onSelect(cluster)}
              aria-pressed={isSelected}
              title={`${cluster.label} — ${cluster.documentCount} ${
                cluster.documentCount === 1 ? "source" : "sources"
              }, ${BAND_LABEL[cluster.band].toLowerCase()}`}
              className={cn(
                "knowledge-cluster-drift group flex flex-col items-center justify-center rounded-full p-3 text-center transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2",
                isSelected ? "shadow-lg" : "hover:shadow-md",
              )}
              style={
                {
                  width: size,
                  height: size,
                  backgroundColor: `${color}14`,
                  border: `2px solid ${isSelected ? color : `${color}66`}`,
                  boxShadow: isSelected ? `0 0 0 4px ${color}22` : undefined,
                  "--drift-duration": `${12 + (index % 5) * 2}s`,
                  "--drift-delay": `${(index % 7) * 0.6}s`,
                  "--drift-x": `${index % 2 === 0 ? 4 : -4}px`,
                  "--drift-y": `${index % 3 === 0 ? -5 : -3}px`,
                } as CSSProperties
              }
            >
              <span
                className="text-lg font-bold leading-none tabular-nums"
                style={{ color }}
              >
                {cluster.documentCount}
              </span>
              <span className="mt-1 line-clamp-2 px-1 text-[11px] font-semibold leading-tight text-gray-700">
                {cluster.label}
              </span>
              {cluster.openQuestionCount > 0 && (
                <span className="mt-1 text-[9px] font-semibold uppercase tracking-wide text-genie-secondary">
                  {cluster.openQuestionCount} open
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default TopicClusterMap;
